import * as React from "react";
import { Routine } from "redux-saga-routines";
import { Action, ActionFunctionAny } from "redux-actions";
import EditFunctionIcon from "@Icons/FontAwesome/EditFunction";
import ProcessingRequiredIcon from "@Icons/FontAwesome/ProcReq";
import { defineMessages, useIntl } from "react-intl";
import { connect } from "react-redux";
import { Form, Formik } from "formik";
import { editSensorFunction, fetchSensors } from "@Safemate/Settings/Store/routines";
import { DeviceFunctionEnum, getFunctionName, UNKNOWN } from "../SensorFunctionEnum";
import { displayFunctionType, getForFunction } from "../SensorFunctionTypeEnum";
import {
  ConfigPage,
  ConfigPanel,
  ConfigHeader,
  HeaderIdentity,
  Avatar,
  IdentityMeta,
  IdentityName,
  IdentitySub,
  ActiveControl,
  ActiveLabelRow,
  ActiveLabel,
  ActiveHint,
  ConfigBody,
  Section,
  SectionHead,
  SectionTitleWrap,
  SectionTitle,
  SectionDesc,
  SectionBody,
  OptionGrid,
  OptionCard,
  OptionIcon,
  OptionText,
  OptionTitle,
  OptionDesc,
  ToggleRow,
  ToggleText,
  ConfigControl,
  TimeRangeRow,
  TimeLabel,
  ConfigTimeInput,
  ConfigActionRow,
} from "../styles";
import Dropdown from "@Elements/Dropdown/Dropdown";
import withFormWrapper from "@Elements/Form/formWrapper";
import { Option } from "@Elements/Dropdown/types";
import { IEventConfig, ISensorDTO } from "../types";
import { deconstructFunction, mapFunction } from "./utils";
import { AppState } from "@Safemate/Store/types";
import { SENSOR_FUNCTION_CONFIG, getPlainFuncIcon } from "../sensorFunction";
import { useHistory, useParams, useRouteMatch } from "react-router";
import { Params } from "../sensorTable";
import { Button } from "react-bootstrap";
import { RouterParams } from "../sensorDetails";
import Loader from "@Elements/Loader";
import { Wrapper } from "../Add/styles";
import { Link } from "react-router-dom";
import { DeviceType } from "../SensorTypeEnum";
import { getIconForType, getTypeLabel } from "../sensorType";
import { Collapse } from "react-collapse";
import Toggle from "@Elements/Switch/Toggle";
import { Active, Delay, DelayBathroom, DelayWanderer } from "@Safemate/Settings/EventConfig/components";
import MovementIcon from "@Icons/Movement";
import NotinbedIcon from "@Icons/Notinbed";
import { Bathroom } from "@Elements/Icon/Icons/FontAwesome";

// ── i18n ──────────────────────────────────────────────────────────────

const msgs = defineMessages({
  updateBtn:           { id: 'universalUpdateBtn',    defaultMessage: 'Oppdater' },
  back:                { id: 'back',                  defaultMessage: 'Tilbake' },
  functionLabel:       { id: 'functionLabel',         defaultMessage: 'Funksjon' },
  functionHint:        { id: 'functionHint',          defaultMessage: 'Velg hva sensoren skal brukes til.' },
  functionTypeLabel:   { id: 'functionTypeLabel',     defaultMessage: 'Funksjonstype' },
  functionTypeHint:    { id: 'functionTypeHint',      defaultMessage: 'Velg hvordan funksjonen skal fungere.' },
  chooseFunction:      { id: 'chooseFunctionLabel',   defaultMessage: 'Velg funksjon' },
  chooseFunctionType:  { id: 'chooseFunctionTypeLabel', defaultMessage: 'Velg funksjonstype' },
  placement:           { id: 'placement',             defaultMessage: 'Plassering' },
  placementHint:       { id: 'placementHint',         defaultMessage: 'Hva skal denne sensoren overvåke?' },
  selectLocation:      { id: 'selectLocation',        defaultMessage: 'Velg lokasjon' },
  deactivate:          { id: 'deactiveSensor',        defaultMessage: 'Deaktiver sensor' },
  delayLabel:          { id: 'delayLabel',            defaultMessage: 'Forsinkelse' },
  delayHint:           { id: 'delayHint',             defaultMessage: 'Hvor lenge hendelsen må vare før det varsles.' },
  activePeriodLabel:   { id: 'activePeriodLabel',     defaultMessage: 'Aktiv periode' },
  activePeriodHint:    { id: 'activePeriodHint',      defaultMessage: 'Tidsrommet sensoren skal varsle innenfor.' },
  from:                { id: 'activePeriod',          defaultMessage: 'Fra' },
  to:                  { id: 'to',                    defaultMessage: 'til' },
  notification:        { id: 'notificationLabel',     defaultMessage: 'Varsling' },
  notificationHint:    { id: 'notificationHint',      defaultMessage: 'Bestem om det skal sendes varsel for denne hendelsen.' },
  settingsLabel:       { id: 'settingsLabel',         defaultMessage: 'Innstillinger' },
  settingsHint:        { id: 'settingsHint',          defaultMessage: 'Tilpass hvordan sensoren varsler.' },
  sensorActive:        { id: 'sensorActiveLabel',     defaultMessage: 'Sensor aktiv' },
  sensorActiveOn:      { id: 'sensorActiveOnHint',    defaultMessage: 'Sensoren er aktiv og varsler i henhold til innstillingene nedenfor.' },
  sensorActiveOff:     { id: 'sensorActiveOffHint',   defaultMessage: 'Sensoren er deaktivert. Innstillingene bevares og kan aktiveres igjen senere.' },
  notifyWanderer:      { id: 'notifyWandererEvent',   defaultMessage: 'Varsle døralarm' },
  notifyBedroom:       { id: 'notifyBedroomEvent',    defaultMessage: 'Varsle ut av soverom' },
  notifyBathroom:      { id: 'notifyBathroomEvent',   defaultMessage: 'Varsle for lenge på bad' },
  wadcHintWanderer:    { id: 'wadcHintWanderer',      defaultMessage: 'Sensoren varsler når døren åpnes av en vandrer.' },
  wadcHintBedroom:     { id: 'wadcHintBedroom',       defaultMessage: 'Sensoren varsler når beboer forlater soverommet.' },
  wadcHintBathroom:    { id: 'wadcHintBathroom',      defaultMessage: 'Sensoren varsler dersom beboer er lenge på badet.' },
});

// ── WADC placement options ─────────────────────────────────────────────

const wadcPlacementOptions: {
  value: DeviceFunctionEnum;
  text: string;
  desc: { id: string; defaultMessage: string };
  Icon: React.ComponentType<any>;
}[] = [
  {
    value: DeviceFunctionEnum.WANDERER_FUNCTION,
    text: `sensorFunction${DeviceFunctionEnum.WANDERER_FUNCTION}`,
    desc: msgs.wadcHintWanderer,
    Icon: MovementIcon,
  },
  {
    value: DeviceFunctionEnum.OUT_OF_BEDROOM,
    text: `sensorFunction${DeviceFunctionEnum.OUT_OF_BEDROOM}`,
    desc: msgs.wadcHintBedroom,
    Icon: NotinbedIcon,
  },
  {
    value: DeviceFunctionEnum.BATHROOM,
    text: `sensorFunction${DeviceFunctionEnum.BATHROOM}`,
    desc: msgs.wadcHintBathroom,
    Icon: Bathroom,
  },
];

// ── Table-row action icons ─────────────────────────────────────────────

interface EditFunctionIconProps {
  sensorId: number;
  initial: boolean;
}

export const EditFunctionAction = ({ sensorId, initial }: EditFunctionIconProps) => {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const { url } = useRouteMatch();
  const params = useParams<Params>();
  const sensorInfoLink = `${url.split(params.name)[0]}config/sensor`;

  return initial
    ? <ProcessingRequiredIcon
        onClick={() => { history.push(`${sensorInfoLink}/${sensorId}`); }}
        medium animate
        title={formatMessage({ id: 'info', defaultMessage: 'Informasjon' })}
      />
    : <EditFunctionIcon
        onClick={() => { history.push(`${sensorInfoLink}/${sensorId}`); }}
        medium animate
        title={formatMessage({ id: 'info', defaultMessage: 'Informasjon' })}
      />;
};

// ── Shared form value types ────────────────────────────────────────────

export interface EditFunctionValues {
  sensorId: number;
  eventConfig: IEventConfig;
  deviceFunction: DeviceFunctionEnum;
  funcType: string;
}

export interface EditFunctionForm {
  sensor: ISensorDTO;
  allowCustomEvents: boolean;
}

// ── Redux wiring ───────────────────────────────────────────────────────

interface EditFunctionProps {
  deviId: number;
  allowCustomEvents: boolean;
  savingFunction: boolean;
  returnPath: string;
  sensors: ISensorDTO[];
  editSensorFunction: Routine<ActionFunctionAny<Action<any>>>;
  fetchSensors: Routine<ActionFunctionAny<Action<any>>>;
}

const mapStateToProps = ({
  settings: {
    sensor: { sensors, savingFunction },
    device: { device: { allowCustomEvents, deviId } }
  }
}: AppState) => ({ deviId, sensors, allowCustomEvents, savingFunction });

const mapDispatchToProps = { editSensorFunction, fetchSensors };

export const EditFunction = connect(mapStateToProps, mapDispatchToProps)(
  ({ deviId, sensors, fetchSensors, editSensorFunction, allowCustomEvents, savingFunction, returnPath }: EditFunctionProps) => {

    const { formatMessage } = useIntl();
    const { id } = useParams<RouterParams>();
    const sensorId = parseInt(id);

    React.useEffect(() => { fetchSensors({ deviId }); }, []);

    if (!sensors || sensors.length === 0) return <Wrapper><Loader /></Wrapper>;
    const sensor = sensors.find(s => s.sensor.deseId === sensorId);
    if (!sensor) return <Wrapper><Loader /></Wrapper>;

    const { functionType, deseId, sensorType, name } = sensor.sensor;
    const isWadc = (DeviceType.WADC_1 as string) === sensorType;

    return (
      <ConfigPage>
        <ConfigPanel>
          <Formik
            enableReinitialize
            initialValues={{
              sensorId: sensor.sensor.deseId,
              eventConfig: sensor.eventConfig,
              deviceFunction: sensor.function.code as DeviceFunctionEnum,
              funcType: functionType,
            }}
            onSubmit={(values) => {
              editSensorFunction({
                sensorId: deseId,
                eventConfig: values.eventConfig,
                deviceFunction: values.deviceFunction,
                funcType: values.funcType,
              });
            }}
          >
            {({ dirty, values, setFieldValue }) => {
              const active = !!values.eventConfig.active;
              return (
                <Form>
                  {/* ── Identity + master active toggle ── */}
                  <ConfigHeader>
                    <HeaderIdentity>
                      <Avatar>{getIconForType(sensor.type.code)}</Avatar>
                      <IdentityMeta>
                        <IdentityName>{name || sensor.type.name}</IdentityName>
                        <IdentitySub>
                          {formatMessage({
                            id: getTypeLabel(sensor.type.code).labelId,
                            defaultMessage: getTypeLabel(sensor.type.code).defaultMessage,
                          })}
                        </IdentitySub>
                      </IdentityMeta>
                    </HeaderIdentity>

                    <ActiveControl>
                      <ActiveLabelRow>
                        <ActiveLabel>{formatMessage(msgs.sensorActive)}</ActiveLabel>
                        <Toggle
                          checked={active}
                          onChange={(value: boolean) => setFieldValue("eventConfig.active", value)}
                        />
                      </ActiveLabelRow>
                      <ActiveHint>
                        {active ? formatMessage(msgs.sensorActiveOn) : formatMessage(msgs.sensorActiveOff)}
                      </ActiveHint>
                    </ActiveControl>
                  </ConfigHeader>

                  {isWadc
                    ? <WadcConfig sensor={sensor} />
                    : <GenericConfig allowCustomEvents={allowCustomEvents} sensor={sensor} />
                  }

                  <ConfigActionRow>
                    <Link to={returnPath}>
                      <Button>{formatMessage(msgs.back)}</Button>
                    </Link>
                    <Button disabled={!dirty} type="submit">
                      {savingFunction ? <Loader /> : formatMessage(msgs.updateBtn)}
                    </Button>
                  </ConfigActionRow>
                </Form>
              );
            }}
          </Formik>
        </ConfigPanel>
      </ConfigPage>
    );
  }
);

// ── WADC sensor configuration ──────────────────────────────────────────

interface WadcConfigProps {
  sensor: ISensorDTO;
}

const WadcConfig = withFormWrapper<EditFunctionValues, WadcConfigProps>(
  ({ formik: { setFieldValue, values: { deviceFunction, eventConfig: { active, notify, schedule } } } }) => {

    const { formatMessage } = useIntl();

    let notifyMsg: { id: string; defaultMessage: string };
    let DelayComponent: React.ComponentType<any>;

    switch (deviceFunction) {
      case DeviceFunctionEnum.BATHROOM:
        notifyMsg      = msgs.notifyBathroom;
        DelayComponent = DelayBathroom;
        break;
      case DeviceFunctionEnum.OUT_OF_BEDROOM:
        notifyMsg      = msgs.notifyBedroom;
        DelayComponent = Delay;
        break;
      default:
        notifyMsg      = msgs.notifyWanderer;
        DelayComponent = DelayWanderer;
    }

    let from = "00:00";
    let to   = "23:59";
    if (schedule && schedule.length > 0) {
      from = schedule[0].fromTime;
      to   = schedule[0].toTime;
    }

    return (
      <ConfigBody>
        {/* 1 · Placement — selectable cards */}
        <Section>
          <SectionHead>
            <SectionTitleWrap>
              <SectionTitle>{formatMessage(msgs.placement)}</SectionTitle>
              <SectionDesc>{formatMessage(msgs.placementHint)}</SectionDesc>
            </SectionTitleWrap>
          </SectionHead>
          <SectionBody>
            <OptionGrid>
              {wadcPlacementOptions.map(({ value, text, desc, Icon }) => {
                const selected = deviceFunction === value;
                return (
                  <OptionCard
                    key={value}
                    type="button"
                    selected={selected}
                    onClick={() => setFieldValue("deviceFunction", value)}
                  >
                    <OptionIcon selected={selected}>
                      <Icon color="textPrimary" size="medium" />
                    </OptionIcon>
                    <OptionText>
                      <OptionTitle>
                        {formatMessage({ id: text, defaultMessage: text })}
                      </OptionTitle>
                      <OptionDesc>{formatMessage(desc)}</OptionDesc>
                    </OptionText>
                  </OptionCard>
                );
              })}
            </OptionGrid>
          </SectionBody>
        </Section>

        {/* Settings collapse when the sensor is inactive */}
        <Collapse isOpened={!!active}>
          {/* 2 · Notification */}
          <Section>
            <SectionHead>
              <SectionTitleWrap>
                <SectionTitle>{formatMessage(msgs.notification)}</SectionTitle>
                <SectionDesc>{formatMessage(msgs.notificationHint)}</SectionDesc>
              </SectionTitleWrap>
            </SectionHead>
            <SectionBody>
              <ToggleRow>
                <Toggle
                  checked={!!notify}
                  onChange={(value: boolean) => setFieldValue("eventConfig.notify", value)}
                />
                <ToggleText>{formatMessage(notifyMsg)}</ToggleText>
              </ToggleRow>
            </SectionBody>
          </Section>

          {/* 3 · Delay */}
          <Section>
            <SectionHead>
              <SectionTitleWrap>
                <SectionTitle>{formatMessage(msgs.delayLabel)}</SectionTitle>
                <SectionDesc>{formatMessage(msgs.delayHint)}</SectionDesc>
              </SectionTitleWrap>
            </SectionHead>
            <SectionBody>
              <ConfigControl>
                <DelayComponent />
              </ConfigControl>
            </SectionBody>
          </Section>

          {/* 4 · Active period */}
          <Section>
            <SectionHead>
              <SectionTitleWrap>
                <SectionTitle>{formatMessage(msgs.activePeriodLabel)}</SectionTitle>
                <SectionDesc>{formatMessage(msgs.activePeriodHint)}</SectionDesc>
              </SectionTitleWrap>
            </SectionHead>
            <SectionBody>
              <TimeRangeRow>
                <TimeLabel>{formatMessage(msgs.from)}</TimeLabel>
                <ConfigTimeInput
                  disabled={!notify}
                  type="time"
                  value={from}
                  onChange={(e) => {
                    const time = e.currentTarget.value;
                    for (let i = 0; i < 7; i++) setFieldValue(`eventConfig.schedule.${i}.fromTime`, time);
                  }}
                />
                <TimeLabel>{formatMessage(msgs.to)}</TimeLabel>
                <ConfigTimeInput
                  disabled={!notify}
                  type="time"
                  value={to}
                  onChange={(e) => {
                    const time = e.currentTarget.value;
                    if (time > from) {
                      for (let i = 0; i < 7; i++) setFieldValue(`eventConfig.schedule.${i}.toTime`, time);
                    }
                  }}
                />
              </TimeRangeRow>
            </SectionBody>
          </Section>
        </Collapse>
      </ConfigBody>
    );
  }
);

// ── Generic sensor configuration ───────────────────────────────────────

const GenericConfig = withFormWrapper<EditFunctionValues, EditFunctionForm>(
  ({ formik: { setFieldValue, values: { deviceFunction, funcType, eventConfig: { active } } }, sensor, allowCustomEvents }) => {

    const { formatMessage } = useIntl();

    const mappedOptions = React.useMemo(() => {
      if (!sensor.type.supportedFunctions) return [];

      let hasCustom = false;
      const functions = sensor.type.supportedFunctions
        .filter(func => func.code !== UNKNOWN)
        .map(func => {
          const functionName = getFunctionName(sensor.type, func, formatMessage);
          if (!functionName) return null;
          if (func.code === DeviceFunctionEnum.CUSTOM_SENSOR_FUNCTION) hasCustom = allowCustomEvents;
          return { text: functionName, value: func.code };
        })
        .filter(func => func && func.value !== DeviceFunctionEnum.CUSTOM_SENSOR_FUNCTION) as Option[];

      if (hasCustom) return functions.concat(getForFunction(DeviceFunctionEnum.CUSTOM_SENSOR_FUNCTION, formatMessage));
      return functions;
    }, [sensor.type]);

    const iconByCode = React.useMemo(() => {
      const map = new Map<string, string>();
      sensor.type.supportedFunctions?.forEach(func => map.set(func.code, func.icon));
      return map;
    }, [sensor.type]);

    const functionTypes = React.useMemo(() => getForFunction(deviceFunction, formatMessage), [deviceFunction]);

    const functionConfig = SENSOR_FUNCTION_CONFIG.get(deviceFunction);
    const showFuncType = displayFunctionType(sensor.type.code, deviceFunction);
    const selectedValue = mapFunction(deviceFunction, funcType);

    // Drop the standalone Active toggle — it is hoisted to the header
    const settings = (functionConfig || []).filter(
      config => !(React.isValidElement(config) && config.type === Active)
    );

    return (
      <ConfigBody>
        {/* 1 · Function — selectable cards */}
        <Section>
          <SectionHead>
            <SectionTitleWrap>
              <SectionTitle>{formatMessage(msgs.functionLabel)}</SectionTitle>
              <SectionDesc>{formatMessage(msgs.functionHint)}</SectionDesc>
            </SectionTitleWrap>
          </SectionHead>
          <SectionBody>
            <OptionGrid>
              {mappedOptions.map(opt => {
                const selected = opt.value === selectedValue;
                const iconStr = iconByCode.get(opt.value as string);
                const iconEl = (iconStr && getPlainFuncIcon(iconStr)) || getIconForType(sensor.type.code);
                return (
                  <OptionCard
                    key={opt.value}
                    type="button"
                    selected={selected}
                    onClick={() => {
                      const { deviceFunction: df, funcType: ft } = deconstructFunction(opt, formatMessage);
                      setFieldValue("deviceFunction", df);
                      setFieldValue("funcType", ft);
                    }}
                  >
                    <OptionIcon selected={selected}>{iconEl}</OptionIcon>
                    <OptionText>
                      <OptionTitle>{opt.text}</OptionTitle>
                    </OptionText>
                  </OptionCard>
                );
              })}
            </OptionGrid>
          </SectionBody>
        </Section>

        {/* 2 · Function type */}
        {showFuncType && (
          <Section>
            <SectionHead>
              <SectionTitleWrap>
                <SectionTitle>{formatMessage(msgs.functionTypeLabel)}</SectionTitle>
                <SectionDesc>{formatMessage(msgs.functionTypeHint)}</SectionDesc>
              </SectionTitleWrap>
            </SectionHead>
            <SectionBody>
              <ConfigControl>
                <Dropdown
                  expand
                  options={functionTypes}
                  initial={sensor.sensor.functionType}
                  title={formatMessage(msgs.chooseFunctionType)}
                  onChange={(value) => setFieldValue("funcType", (value as Option).value)}
                />
              </ConfigControl>
            </SectionBody>
          </Section>
        )}

        {/* Settings — collapses when the sensor is inactive */}
        {settings.length > 0 && (
          <Collapse isOpened={!!active}>
            <Section>
              <SectionHead>
                <SectionTitleWrap>
                  <SectionTitle>{formatMessage(msgs.settingsLabel)}</SectionTitle>
                  <SectionDesc>{formatMessage(msgs.settingsHint)}</SectionDesc>
                </SectionTitleWrap>
              </SectionHead>
              <SectionBody>
                {settings.map((config, i) => (
                  <div key={i}>{config}</div>
                ))}
              </SectionBody>
            </Section>
          </Collapse>
        )}
      </ConfigBody>
    );
  }
);

